// src/lib/api.ts
// Opus Job Operator API client implementation (via proxy server)
// The proxy server handles all Opus API calls to avoid CORS issues

import type {
  TriageRequestPayload,
  TriageResponsePayload,
} from "../types/triage";

// Proxy server configuration
// In production (Vercel), use relative path. In development, use localhost
const PROXY_BASE_URL = import.meta.env.PROD
  ? '' // Use relative path in production (Vercel serverless functions)
  : (import.meta.env.VITE_PROXY_URL || 'http://localhost:3000');
const APP_VERSION = import.meta.env.VITE_APP_VERSION as string;

export class ApiError extends Error {
  statusCode?: number;
  responseBody?: any;

  constructor(
    message: string,
    statusCode?: number,
    responseBody?: any
  ) {
    super(message);
    this.name = "ApiError";
    this.statusCode = statusCode;
    this.responseBody = responseBody;
  }
}

/**
 * Public API: Post triage data to Opus and get analysis result
 * This is the main function called from the UI
 *
 * Now calls the proxy server which handles all Opus API interactions
 */
export async function postToOpus(
  payload: TriageRequestPayload
): Promise<TriageResponsePayload> {
  try {
    console.log('📤 Sending triage request to proxy server...');

    const response = await fetch(`${PROXY_BASE_URL}/api/triage`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-App-Version': APP_VERSION || '0.1.0',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        errorData
      );
    }

    const result: TriageResponsePayload = await response.json();
    console.log('✅ Triage result received:', result);
    return result;

  } catch (error) {
    console.error('❌ Proxy API error:', error);

    if (error instanceof ApiError) {
      throw error;
    }

    if (error instanceof TypeError) {
      throw new ApiError("Network error: Please check your connection");
    }

    if ((error as any).name === "AbortError") {
      throw new ApiError("Request timeout: Please try again");
    }

    throw new ApiError(`Unexpected error: ${(error as Error).message}`);
  }
}

/**
 * Retry wrapper with exponential backoff
 * For transient failures (5xx errors)
 */
export async function postToOpusWithRetry(
  payload: TriageRequestPayload,
  maxRetries: number = 3
): Promise<TriageResponsePayload> {
  let lastError: Error;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await postToOpus(payload);
    } catch (error) {
      lastError = error as Error;

      // Don't retry on client errors (4xx)
      if (error instanceof ApiError && error.statusCode && error.statusCode < 500) {
        throw error;
      }

      // Wait before retry (exponential backoff)
      if (attempt < maxRetries - 1) {
        const waitMs = Math.pow(2, attempt) * 1000;
        console.log(`⚠️ Retry attempt ${attempt + 1}/${maxRetries} in ${waitMs}ms...`);
        await new Promise(resolve => setTimeout(resolve, waitMs));
      }
    }
  }

  throw lastError!;
}
